import BaseMygocuApiService from './BaseMygocuApiService';

const VIAJES_GRUPO_API_VERSION = 'v1';
const VIAJES_GRUPO_API_BASEPATH_URL = '/viajesGrupo/' + VIAJES_GRUPO_API_VERSION;

function buildViajeQueryParams(codigoViaje, codigoPrevisualizacion) {
  const params = {};
  if (codigoViaje) params.codigo = codigoViaje;
  if (codigoPrevisualizacion) params.codigoPrevisualizacion = codigoPrevisualizacion;
  return params;
}

class ViajesGrupoApiService {
  async getFiltroViajes() {
    return await BaseMygocuApiService.get(VIAJES_GRUPO_API_BASEPATH_URL.concat('/filtroViajes'));
  }

  async getViajes() {
    return await BaseMygocuApiService.get(VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes'));
  }

  async getViaje(viajeId, codigoViaje, codigoPrevisualizacion) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  async incrementaVisitasViaje(viajeId, codigoViaje, codigoPrevisualizacion) {
    return await BaseMygocuApiService.put(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/numVisitas'),
      {},
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  async rechazarInvitacionGrupo(viajeId, invitacionGrupoId, vgInvitacionRechazarDTO) {
    return await BaseMygocuApiService.put(
      VIAJES_GRUPO_API_BASEPATH_URL.concat(
        '/viajes/' + viajeId + '/invitacionGrupo/' + invitacionGrupoId + '/rechazar'
      ),
      vgInvitacionRechazarDTO
    );
  }

  async getAlojamientosViaje(viajeId, codigoViaje = null, codigoPrevisualizacion = null) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/alojamientos'),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  /**
   * Devuelve todos los extras disponibles para una viaje agrupados por tipo
   *
   * @param viajeId viaje Id
   * @returns {Promise<*>} VGExtrasCategorizadosDTO Agrupacion de extras de un mismo tipo
   */
  async getServicios(viajeId, codigoViaje = null, codigoPrevisualizacion = null) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/servicios'),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  /**
   * Indica si hay disponibilidad de servicios de paquete (incl. upselling) para poder reservar.
   *
   * @returns {Promise<boolean>} true = Reservar, false = Lista de espera
   */
  async getDisponibilidadServicios(viajeId, codigoViaje = null, codigoPrevisualizacion = null) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/servicios/disponibilidad'),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  async checkCompatibility(viajeId, codigoViaje = null, codigoPrevisualizacion = null) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat(`/viajes/${viajeId}/compatibility`),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  async getValidarCupo(
    viajeId,
    alojamientoId,
    tipoHabitacionId,
    numPlazas,
    plazasReales,
    precioCupoSinMarkupProrrateado,
    tipoRegimenId,
    integrado,
    cacheTimestamp,
    cupoId,
    codigoViaje = null,
    codigoPrevisualizacion = null
  ) {
    const params = {
      tipoHabitacionId: tipoHabitacionId ?? '',
      numPlazas,
      plazasReales,
      precioCupoSinMarkupProrrateado: precioCupoSinMarkupProrrateado ?? '',
      tipoRegimenId: tipoRegimenId ?? '',
      integrado,
      cacheTimestamp,
      cupoId: cupoId ?? '',
      ...buildViajeQueryParams(codigoViaje, codigoPrevisualizacion),
    };

    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat(`/viajes/${viajeId}/alojamientos/${alojamientoId}/validaCupo`),
      params
    );
  }

  async validarDescuento(viajeId, codigo, reservasIds) {
    var params = new URLSearchParams();
    params.append('codigo', codigo);
    reservasIds.forEach(data => {
      params.append('reservasIds[]', data);
    });

    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/validarDescuento'),
      params
    );
  }

  /**
   * Crea una reserva temporal (bloqueada) que en caso de no confirmarse realizando el pago de la misma en un periodo
   * de tiempo determinado, se borrara automaticamente liberando la plaza.
   *
   * @param viajeId Id del viaje
   * @param vgCreacionReservaDTO VGCreacionReservaDTO
   * @returns {Promise<*>} VGReservaDTO
   */
  async crearGrupoReservas(viajeId, vgCreacionReservaDTO) {
    return await BaseMygocuApiService.post(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/crearGrupoReservas'),
      vgCreacionReservaDTO
    );
  }

  /**
	 * Devuelve los datos de la reserva del usuario para el viaje pasado para retomar el proceso de confirmacion
     de la misma siempre y cuando este en estado BLOQUEADA
	 *
	 * @param viajeId Id del viaje
	 * @returns {Promise<*>} VGReservaDTO
	 */
  async recuperarGrupoReservas(viajeId) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/recuperarGrupoReservas')
    );
  }

  /**
   * Modifica una reserva temporal (bloqueada).
   *
   * @param viajeId Ide del viaje
   * @param vgModificacionReservaDTO VGModificacionReservaDTO
   * @returns {Promise<*>} VGReservaDTO
   */
  async modificarGrupoReservas(viajeId, vgModificacionReservaDTO) {
    return await BaseMygocuApiService.post(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/modificarGrupoReservas'),
      vgModificacionReservaDTO
    );
  }

  /**
   * Elimina la reserva del usuario para el viaje pasado siempre y cuando este en estado BLOQUEADA
   *
   * @param viajeId Id del viaje
   * @returns {Promise<*>} 200-OK
   */
  async descartarGrupoReservas(viajeId) {
    return await BaseMygocuApiService.delete(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/descartarGrupoReservas')
    );
  }

  /**
   * Crea o modifica una lista de espera para un usuario y un viaje
   *
   * @param vgListaEsperaDTO datos de la lista de espera a agreagr o modificar
   * @returns {Promise<*>}
   */
  async agregarListaEspera(vgListaEsperaDTO) {
    return await BaseMygocuApiService.post(VIAJES_GRUPO_API_BASEPATH_URL.concat('/listaEspera'), vgListaEsperaDTO);
  }

  /**
   * Vota en un viaje de una oferta
   *
   * @param idOfertaViaje id del asociado al viaje a votar en la oferta correspondiente
   * @returns {Promise<*>}
   */
  async votarOferta(idOfertaViaje) {
    return await BaseMygocuApiService.get(VIAJES_GRUPO_API_BASEPATH_URL.concat('/ofertas/votar/', idOfertaViaje));
  }

  /**
   * Devuelve los módulos del builder de un viaje
   *
   * @param viajeId Id del viaje
   * @param {string} [codigo] Código del viaje (solo si el viaje es privado)
   * @param {string} [codigoPrevisualizacion] Código de previsualización del viaje
   * @returns {Promise<VGBuilderModuleDetailDTO[]>} El detalle del builder modules de un viaje
   */
  async getBuilderModule(viajeId, codigoViaje = null, codigoPrevisualizacion = null) {
    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/builderModules'),
      buildViajeQueryParams(codigoViaje, codigoPrevisualizacion)
    );
  }

  /**
   * Devuelve el detalle de las importaciones de reserva de un viaje
   *
   * @param {number} viajeId Id del viaje
   * @param {string} [codigo] Código del viaje (solo si el viaje es privado)
   * @param {string} [codigoPrevisualizacion] Código de previsualización del viaje
   * @returns {Promise<*>} Lista de importaciones de reserva
   */
  async getImportarReservas(viajeId, codigo, codigoPrevisualizacion) {
    const params = {};
    if (codigo) params.codigo = codigo;
    if (codigoPrevisualizacion) params.codigoPrevisualizacion = codigoPrevisualizacion;

    return await BaseMygocuApiService.get(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/importarReservas'),
      params
    );
  }

  /**
   * Guarda el email en los leads de un viaje
   *
   * @param {number} viajeId Id del viaje
   * @param {Object} vgLeadDTO Datos del lead a guardar
   * @returns {Promise<*>} 200-OK si el lead fue guardado correctamente
   */
  async guardarLead(viajeId, vgLeadDTO) {
    return await BaseMygocuApiService.post(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/leads'),
      vgLeadDTO
    );
  }

  /**
   * Devuelve la url del resumen del viaje en pdf
   *
   * @param {number} viajeId Id del viaje
   * @param {Object} docAPeticionRequestDTO Informacion para obtener el resumen del viaje
   * @returns {Promise<*>} DocAPeticionDTO con la url del resumen del viaje
   */
  async getResumenViajePdf(viajeId, docAPeticionRequestDTO) {
    return await BaseMygocuApiService.post(
      VIAJES_GRUPO_API_BASEPATH_URL.concat('/viajes/' + viajeId + '/resumenPdf'),
      docAPeticionRequestDTO
    );
  }
}

export default new ViajesGrupoApiService();
